import React from 'react';
import { Shield, Cpu, Users, ToggleLeft, ToggleRight, RefreshCw } from 'lucide-react';

/**
 * SpendControls — tier limits, auto-topup status, per-user budgets.
 *
 * Props:
 *   dashboard  — DashboardResponse
 *   loading    — boolean
 */
function SpendControls({ dashboard, loading }) {
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-4 bg-gray-200 rounded w-full" />
        ))}
      </div>
    );
  }

  if (!dashboard) return null;

  const tier = dashboard.tier?.toLowerCase();
  const isTeamOrEnt = tier === 'team' || tier === 'enterprise';

  const accessLabels = {
    standard: 'Standard models only',
    advanced: 'Advanced models',
    full: 'Full model access (all tiers)',
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-5">
      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Spend Controls</h3>

      {/* Tier limits */}
      <div className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-1.5 text-gray-600">
            <Shield className="w-4 h-4" /> Concurrent runs
          </span>
          <span className="font-medium text-gray-900">
            {dashboard.concurrency_limit ?? 'Unlimited'}
          </span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-1.5 text-gray-600">
            <Cpu className="w-4 h-4" /> Model access
          </span>
          <span className="font-medium text-gray-900">
            {accessLabels[dashboard.model_access_level] ?? dashboard.model_access_level}
          </span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-1.5 text-gray-600">
            <Shield className="w-4 h-4" /> Custom tools
          </span>
          <span className={`font-medium ${dashboard.custom_tools_enabled ? 'text-green-600' : 'text-gray-400'}`}>
            {dashboard.custom_tools_enabled ? 'Enabled' : 'Not available'}
          </span>
        </div>
      </div>

      <div className="border-t border-gray-100" />

      {/* Auto-topup */}
      <div>
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="flex items-center gap-1.5 text-gray-600">
            <RefreshCw className="w-4 h-4" /> Auto top-up
          </span>
          {dashboard.auto_topup_enabled ? (
            <span className="flex items-center gap-1 text-green-600 font-medium">
              <ToggleRight className="w-4 h-4" /> On
            </span>
          ) : (
            <span className="flex items-center gap-1 text-gray-400 font-medium">
              <ToggleLeft className="w-4 h-4" /> Off
            </span>
          )}
        </div>
        {dashboard.auto_topup_enabled && (
          <p className="text-xs text-gray-400 ml-5.5">
            {dashboard.auto_topup_daily_remaining} top-up{dashboard.auto_topup_daily_remaining !== 1 ? 's' : ''} remaining today
          </p>
        )}
      </div>

      {/* Per-user budgets — TEAM/ENTERPRISE only */}
      {isTeamOrEnt && (
        <>
          <div className="border-t border-gray-100" />
          <div>
            <p className="flex items-center gap-1.5 text-sm text-gray-600 mb-2">
              <Users className="w-4 h-4" /> Per-user budgets
            </p>
            <p className="text-xs text-gray-400">
              Configure per-user monthly credit budgets in your subscription settings.
              Budgets are enforced at workflow start.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export default SpendControls;
